import React, { useState, useEffect } from 'react';
import {
  Save, Edit2, Trash2, Upload, Loader2, Download,
  Image as ImageIcon, StickyNote, FileText, Camera
} from 'lucide-react';
import { getVolunteerStatusColor } from '../../../utils/statusColors';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_ALLOC_FOOD_PCT = 60;
const DEFAULT_ALLOC_HYGIENE_PCT = 30;
const DEFAULT_ALLOC_DISTRIBUTION_PCT = 10;

export const SiteImagesTab = ({ ctx }) => {
  const { siteImages, siteImagePositions, uploadingSiteImage, handleSiteImageUpload, handleDeleteSiteImage } = ctx;

  return (
    <div className="space-y-6" data-testid="site-images-tab">
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
        <h3 className="font-heading font-bold mb-2">Manage Site Images</h3>
        <p className="text-sm text-[#4B5563] mb-6">
          Upload photos to replace placeholder sections on the public website. Images appear immediately after upload.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {siteImagePositions.map((pos) => {
            const existingImage = siteImages.find(img => img.key === pos.key);
            return (
              <div key={pos.key} className="border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid={`site-image-${pos.key}`}>
                {existingImage ? (
                  <div className="relative">
                    <img src={`${BACKEND_URL}/api/uploads/${existingImage.filename}`} alt={pos.label} className="w-full h-40 object-cover" />
                    <div className="absolute top-2 right-2 flex gap-1">
                      <label className="bg-white/90 backdrop-blur px-2 py-1 rounded text-xs cursor-pointer hover:bg-white flex items-center gap-1">
                        <Upload className="w-3 h-3" /> Replace
                        <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.gif,.webp"
                          onChange={(e) => { if (e.target.files[0]) handleSiteImageUpload(pos.key, e.target.files[0]); }} />
                      </label>
                      <button onClick={() => handleDeleteSiteImage(pos.key)}
                        className="bg-white/90 backdrop-blur px-2 py-1 rounded text-xs text-red-600 hover:bg-white">Remove</button>
                    </div>
                  </div>
                ) : (
                  <div className="h-40 bg-[#F8F9FA] border-2 border-dashed border-[#E2E8F0] flex flex-col items-center justify-center">
                    {uploadingSiteImage === pos.key ? (
                      <Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" />
                    ) : (
                      <>
                        <ImageIcon className="w-6 h-6 text-[#CBD5E1] mb-2" />
                        <label className="cursor-pointer text-sm text-[#BA1826] hover:underline font-medium">
                          Upload Photo
                          <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.gif,.webp"
                            onChange={(e) => { if (e.target.files[0]) handleSiteImageUpload(pos.key, e.target.files[0]); }} />
                        </label>
                      </>
                    )}
                  </div>
                )}
                <div className="p-3">
                  <p className="text-sm font-medium text-[#0A0A0A]">{pos.label}</p>
                  <p className="text-xs text-[#9CA3AF]">{pos.hint}</p>
                  {existingImage && (
                    <p className="text-xs text-green-600 mt-1">Uploaded: {existingImage.updated_at ? new Date(existingImage.updated_at).toLocaleDateString() : 'Active'}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {siteImagePositions.length === 0 && (
          <p className="text-sm text-[#9CA3AF] text-center py-8">No image positions configured.</p>
        )}
      </div>
    </div>
  );
};

export const AppealTab = ({ ctx }) => {
  const { appealForm, setAppealForm, saveAppeal, appealLoading } = ctx;

  return (
    <div className="space-y-6" data-testid="appeal-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg font-bold">Urgent Appeal Banner</h2>
        <span className={`text-xs font-semibold px-2 py-1 rounded ${appealForm.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
          {appealForm.is_active ? 'Active' : 'Inactive'}
        </span>
      </div>
      <p className="text-xs text-[#4B5563] -mt-4">
        Enable to show a prominent banner on all public pages. Progress is tied to real donation totals.
      </p>
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 space-y-4">
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-[#4B5563]">Banner Active</label>
          <button onClick={() => setAppealForm(f => ({ ...f, is_active: !f.is_active }))}
            className={`w-10 h-5 rounded-full transition-colors relative ${appealForm.is_active ? 'bg-[#BA1826]' : 'bg-gray-300'}`}
            data-testid="appeal-toggle">
            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${appealForm.is_active ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#4B5563] mb-1">Title</label>
          <input type="text" value={appealForm.title || ''} onChange={(e) => setAppealForm(f => ({ ...f, title: e.target.value }))}
            placeholder="e.g. Urgent: Hurricane Relief" className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm" data-testid="appeal-title-input" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#4B5563] mb-1">Message</label>
          <textarea value={appealForm.message || ''} onChange={(e) => setAppealForm(f => ({ ...f, message: e.target.value }))}
            placeholder="A short message explaining the urgency..." rows={2} className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm resize-none" data-testid="appeal-message-input" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-[#4B5563] mb-1">Goal Amount ($)</label>
            <input type="number" value={appealForm.goal_amount || 0} onChange={(e) => setAppealForm(f => ({ ...f, goal_amount: parseFloat(e.target.value) || 0 }))}
              className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm" data-testid="appeal-goal-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4B5563] mb-1">CTA Button Text</label>
            <input type="text" value={appealForm.cta_text || 'Donate Now'} onChange={(e) => setAppealForm(f => ({ ...f, cta_text: e.target.value }))}
              className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm" data-testid="appeal-cta-input" />
          </div>
        </div>
        <button onClick={saveAppeal} disabled={appealLoading} className="btn-primary text-sm disabled:opacity-50" data-testid="appeal-save-btn">
          {appealLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> Save Appeal Settings</>}
        </button>
      </div>
    </div>
  );
};

export const VolunteersTab = ({ ctx }) => {
  const {
    volunteers, filteredVolunteers, volunteerFilter, setVolunteerFilter,
    editingVolunteer, setEditingVolunteer, setVolunteers,
    updateVolunteer, deleteVolunteer
  } = ctx;

  return (
    <div className="space-y-4" data-testid="volunteers-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg font-bold">Volunteer Submissions</h2>
        <span className="text-xs bg-[#FEE2E2] text-[#BA1826] px-2 py-1 rounded font-semibold">{volunteers.length} total</span>
      </div>
      <div className="flex gap-2 flex-wrap mb-2">
        {['all', 'new', 'contacted', 'active', 'inactive'].map(f => (
          <button key={f} onClick={() => setVolunteerFilter(f)}
            className={`text-xs px-3 py-1 rounded-full border transition-colors ${volunteerFilter === f ? 'bg-[#BA1826] text-white border-[#BA1826]' : 'bg-white text-[#4B5563] border-[#E5E7EB] hover:border-[#BA1826]'}`}
            data-testid={`volunteer-filter-${f}`}
          >{f.charAt(0).toUpperCase() + f.slice(1)}</button>
        ))}
      </div>
      {filteredVolunteers.length === 0 ? (
        <p className="text-sm text-[#4B5563] text-center py-8">No volunteer submissions{volunteerFilter !== 'all' ? ` with status "${volunteerFilter}"` : ''} yet.</p>
      ) : (
        <div className="space-y-3">
          {filteredVolunteers.map(vol => (
            <div key={vol.id} className="bg-white border border-[#E5E7EB] rounded-lg p-4" data-testid={`volunteer-card-${vol.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-semibold text-sm text-[#0A0A0A]">{vol.name}</h3>
                  <p className="text-xs text-[#4B5563]">{vol.email}{vol.phone ? ` | ${vol.phone}` : ''}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${getVolunteerStatusColor(vol.status)}`}>{vol.status}</span>
                  <button onClick={() => deleteVolunteer(vol.id)} className="text-gray-400 hover:text-red-500" data-testid={`volunteer-delete-${vol.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              {vol.skills && <p className="text-xs text-[#4B5563] mb-1"><span className="font-medium">Skills:</span> {vol.skills}</p>}
              {vol.availability && <p className="text-xs text-[#4B5563] mb-1"><span className="font-medium">Availability:</span> {vol.availability}</p>}
              {vol.message && <p className="text-xs text-[#4B5563] mb-2 italic">"{vol.message}"</p>}
              <p className="text-[10px] text-gray-400 mb-2">Submitted: {vol.created_at ? new Date(vol.created_at).toLocaleDateString() : 'N/A'}</p>

              {editingVolunteer === vol.id ? (
                <div className="border-t border-[#E5E7EB] pt-3 mt-2 space-y-2">
                  <div>
                    <label className="block text-[10px] font-medium text-[#4B5563] mb-0.5">Status</label>
                    <select value={vol.status} onChange={(e) => setVolunteers(vs => vs.map(v => v.id === vol.id ? { ...v, status: e.target.value } : v))}
                      className="w-full px-2 py-1.5 border border-[#E5E7EB] rounded-sm text-xs" data-testid={`volunteer-status-select-${vol.id}`}>
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-medium text-[#4B5563] mb-0.5">Admin Notes</label>
                    <textarea value={vol.admin_notes || ''} onChange={(e) => setVolunteers(vs => vs.map(v => v.id === vol.id ? { ...v, admin_notes: e.target.value } : v))}
                      rows={2} className="w-full px-2 py-1.5 border border-[#E5E7EB] rounded-sm text-xs resize-none" placeholder="Internal notes about this volunteer..."
                      data-testid={`volunteer-notes-input-${vol.id}`} />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => updateVolunteer(vol.id, { status: vol.status, admin_notes: vol.admin_notes })}
                      className="text-xs bg-[#BA1826] text-white px-3 py-1 rounded hover:bg-[#A41623]" data-testid={`volunteer-save-${vol.id}`}>
                      <Save className="w-3 h-3 inline mr-1" /> Save
                    </button>
                    <button onClick={() => setEditingVolunteer(null)} className="text-xs text-[#4B5563] hover:underline">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  {vol.admin_notes && <p className="text-[10px] text-[#4B5563] flex-1"><StickyNote className="w-3 h-3 inline mr-1 text-yellow-500" />{vol.admin_notes}</p>}
                  <button onClick={() => setEditingVolunteer(vol.id)} className="text-xs text-[#BA1826] hover:underline" data-testid={`volunteer-edit-${vol.id}`}>
                    <Edit2 className="w-3 h-3 inline mr-1" /> Manage
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const ReceiptsTab = ({ receipts }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [localReceipts, setLocalReceipts] = useState(receipts);

  useEffect(() => { setLocalReceipts(receipts); }, [receipts]);

  const handleUpload = async (donationId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(donationId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/admin/receipts/${donationId}/documents`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      setLocalReceipts(prev => prev.map(r =>
        r.id === donationId ? { ...r, documents: [...(r.documents || []), res.data] } : r
      ));
    } catch (err) { alert(err.response?.data?.detail || 'Upload failed'); }
    setUploading(null);
    e.target.value = '';
  };

  const handleDelete = async (donationId, docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/receipts/${donationId}/documents/${docId}`, { withCredentials: true });
      setLocalReceipts(prev => prev.map(r =>
        r.id === donationId ? { ...r, documents: (r.documents || []).filter(d => d.id !== docId) } : r
      ));
    } catch { alert('Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="receipts-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg font-bold">Donation Tax Receipts</h2>
        <span className="text-xs bg-[#FEE2E2] text-[#BA1826] px-2 py-1 rounded font-semibold">{localReceipts.length} receipts</span>
      </div>
      <p className="text-xs text-[#4B5563] -mt-2">
        PDF tax receipts are auto-generated for completed donations. You can also upload receipt images or documents for each donation.
      </p>
      {localReceipts.length === 0 ? (
        <p className="text-sm text-[#4B5563] text-center py-8">No completed donations with receipts yet.</p>
      ) : (
        <div className="space-y-2">
          {localReceipts.map(d => {
            const docs = d.documents || [];
            const isExpanded = expandedId === d.id;
            return (
              <div key={d.id} className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid={`receipt-row-${d.id}`}>
                <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#F8F9FA]" onClick={() => setExpandedId(isExpanded ? null : d.id)}>
                  <div className="flex-1 flex items-center gap-4 text-xs">
                    <span className="text-[#4B5563] w-20">{d.created_at ? new Date(d.created_at).toLocaleDateString() : 'N/A'}</span>
                    <span className="font-medium w-32 truncate">{d.donor_name || 'Anonymous'}</span>
                    <span className="text-[#4B5563] w-40 truncate hidden md:block">{d.donor_email || '—'}</span>
                    <span className="font-semibold text-[#BA1826]">${d.amount?.toFixed(2)}</span>
                    <span className="text-[#4B5563] hidden sm:block">{d.payment_type || (d.is_recurring ? 'Recurring' : 'One-time')}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {docs.length > 0 && (
                      <span className="text-[10px] font-semibold bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{docs.length} doc{docs.length !== 1 ? 's' : ''}</span>
                    )}
                    <label className="text-[10px] font-semibold text-white bg-[#0A0A0A] hover:bg-[#333] px-2.5 py-1 rounded flex items-center gap-1 cursor-pointer"
                      onClick={(e) => e.stopPropagation()} data-testid={`receipt-camera-btn-${d.id}`}>
                      {uploading === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
                      Scan
                      <input type="file" accept="image/*" capture="environment" className="hidden"
                        onChange={(e) => handleUpload(d.id, e)} disabled={uploading === d.id} />
                    </label>
                    <label className="text-[10px] font-semibold text-white bg-[#BA1826] hover:bg-[#A41623] px-2.5 py-1 rounded flex items-center gap-1 cursor-pointer"
                      onClick={(e) => e.stopPropagation()} data-testid={`receipt-upload-btn-${d.id}`}>
                      {uploading === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                      Upload
                      <input type="file" accept="image/*,.pdf" className="hidden"
                        onChange={(e) => handleUpload(d.id, e)} disabled={uploading === d.id} />
                    </label>
                    <a href={`${BACKEND_URL}/api/public/receipt/${d.id}`} target="_blank" rel="noopener noreferrer"
                      className="text-[10px] font-semibold text-[#BA1826] hover:underline flex items-center gap-1 px-2 py-1 border border-[#E5E7EB] rounded"
                      onClick={(e) => e.stopPropagation()} data-testid={`receipt-download-${d.id}`}>
                      <Download className="w-3 h-3" /> PDF
                    </a>
                    <span className="text-[#9CA3AF] text-xs">{isExpanded ? '▲' : '▼'}</span>
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-4 pb-4 pt-1 border-t border-[#F1F5F9] bg-[#FAFAFA]" data-testid={`receipt-expanded-${d.id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-semibold text-[#0A0A0A]">Uploaded Documents</h4>
                      <div className="flex items-center gap-2">
                        <label className="text-[10px] font-semibold text-[#0A0A0A] hover:text-[#BA1826] cursor-pointer flex items-center gap-1 border border-[#E5E7EB] rounded px-2 py-1">
                          {uploading === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
                          Take Photo
                          <input type="file" accept="image/*" capture="environment" className="hidden"
                            onChange={(e) => handleUpload(d.id, e)} disabled={uploading === d.id} />
                        </label>
                        <label className="text-[10px] font-semibold text-[#BA1826] hover:underline cursor-pointer flex items-center gap-1">
                          {uploading === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                          Upload File
                          <input type="file" accept="image/*,.pdf" className="hidden"
                            onChange={(e) => handleUpload(d.id, e)} disabled={uploading === d.id}
                            data-testid={`receipt-upload-${d.id}`} />
                        </label>
                      </div>
                    </div>
                    {docs.length === 0 ? (
                      <p className="text-[10px] text-[#9CA3AF] text-center py-3">No documents uploaded yet.</p>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {docs.map(doc => (
                          <div key={doc.id} className="border border-[#E5E7EB] rounded-lg overflow-hidden bg-white group relative" data-testid={`doc-${doc.id}`}>
                            {doc.file_type === 'pdf' ? (
                              <div className="h-20 bg-[#F1F5F9] flex items-center justify-center">
                                <FileText className="w-8 h-8 text-[#BA1826]" />
                              </div>
                            ) : (
                              <div className="h-20">
                                <img src={doc.url} alt={doc.filename} className="w-full h-full object-cover" />
                              </div>
                            )}
                            <div className="p-2">
                              <p className="text-[9px] text-[#4B5563] truncate">{doc.filename}</p>
                              <div className="flex items-center justify-between mt-1">
                                <a href={doc.url} target="_blank" rel="noopener noreferrer"
                                  className="text-[9px] text-[#BA1826] font-semibold hover:underline">View</a>
                                <button onClick={() => handleDelete(d.id, doc.id)}
                                  className="text-[9px] text-red-500 hover:text-red-700" data-testid={`doc-delete-${doc.id}`}>Delete</button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const AllocationTab = ({ ctx }) => {
  const { allocSettings, setAllocSettings, allocSaving, saveAllocSettings, updateManualPct } = ctx;

  return (
    <div className="space-y-6" data-testid="allocation-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg font-bold">Fund Allocation Breakdown</h2>
      </div>
      <p className="text-xs text-[#4B5563] -mt-4">
        Controls the "Where Your Donation Goes" chart on the Donate page. In <strong>auto</strong> mode, percentages are calculated from real expense data in the Finance system. In <strong>manual</strong> mode, you set custom percentages.
      </p>
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 space-y-5">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-[#4B5563]">Mode:</label>
          <div className="flex gap-2">
            {['auto', 'manual'].map(m => (
              <button key={m} onClick={() => setAllocSettings(prev => ({ ...prev, mode: m }))}
                className={`text-xs px-4 py-1.5 rounded-full border transition-colors font-medium ${
                  allocSettings.mode === m ? 'bg-[#BA1826] text-white border-[#BA1826]' : 'bg-white text-[#4B5563] border-[#E5E7EB] hover:border-[#BA1826]'
                }`}
                data-testid={`alloc-mode-${m}`}>
                {m === 'auto' ? 'Auto (from expenses)' : 'Manual'}
              </button>
            ))}
          </div>
        </div>

        {allocSettings.mode === 'auto' ? (
          <div className="bg-[#F8F9FA] rounded-lg p-4">
            <p className="text-xs text-[#4B5563] mb-3">
              Percentages are auto-calculated by mapping expense categories to these 3 buckets.
              If no expense data exists, the default fallback ({DEFAULT_ALLOC_FOOD_PCT} / {DEFAULT_ALLOC_HYGIENE_PCT} / {DEFAULT_ALLOC_DISTRIBUTION_PCT}) is shown.
            </p>
            <div className="space-y-2 text-xs">
              <p><strong className="text-[#BA1826]">Food & supplies:</strong> Food Assistance, Meals, Supplies</p>
              <p><strong className="text-[#FCA5A5]">Hygiene essentials:</strong> Hygiene Items</p>
              <p><strong className="text-[#FECACA]">Distribution:</strong> Travel, Hotel, Events, Admin/Operations, Other</p>
            </div>
            <p className="text-[10px] text-[#9CA3AF] mt-3">
              Tip: Log expenses in the Finance tab with accurate categories for real-time updates.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-[#4B5563]">Set custom percentages (should sum to 100%):</p>
            {(allocSettings.manual_buckets || []).map(b => (
              <div key={b.key} className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: b.color }} />
                <span className="text-sm font-medium text-[#0A0A0A] w-36">{b.label}</span>
                <div className="flex items-center gap-1">
                  <input type="number" min="0" max="100" value={b.percentage}
                    onChange={(e) => updateManualPct(b.key, e.target.value)}
                    className="w-16 px-2 py-1.5 border border-[#E5E7EB] rounded-sm text-sm text-center"
                    data-testid={`alloc-pct-${b.key}`} />
                  <span className="text-sm text-[#4B5563]">%</span>
                </div>
              </div>
            ))}
            {(() => {
              const total = (allocSettings.manual_buckets || []).reduce((s, b) => s + b.percentage, 0);
              return total !== 100 ? (
                <p className="text-xs text-red-500 font-medium">Total: {total}% — must equal 100%</p>
              ) : (
                <p className="text-xs text-green-600 font-medium">Total: 100%</p>
              );
            })()}
          </div>
        )}

        <button onClick={saveAllocSettings} disabled={allocSaving}
          className="btn-primary text-sm disabled:opacity-50" data-testid="alloc-save-btn">
          {allocSaving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> Save Settings</>}
        </button>
      </div>
    </div>
  );
};
